import { useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { showConfirm, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { SsoPlayersConsent } from '@/features/auth/components';
import { WelcomeScreen } from '@/features/auth/screens/welcome-screen';
import {
  abortInteraction,
  confirmInteraction,
  getInteraction,
  submitInteractionLogin,
} from '@/features/auth/services/oidc-interaction';
import { getAuthToken, getAuthUser } from '@/libs/firebase/auth/auth-helpers';
import { captureException } from '@/libs/logger';
import { useOidcInteractionStore, useSessionStore } from '@/stores';
import { colors } from '@/theme/colors';

const strings = STRINGS.auth.sso;

type Phase =
  | 'resolving'
  | 'awaiting-login'
  | 'awaiting-confirm'
  | 'awaiting-players-consent'
  | 'completing'
  | 'error';

/**
 * OIDC interaction UI. The IdP (guardian-view-be) redirects the browser here at
 * `/sso-login/:uid` when it needs the user to log in or consent. We reuse the app's own
 * auth entry (WelcomeScreen → Login/SignUp), then hand the resulting Firebase ID token
 * back to the IdP, which resumes the authorization flow and redirects the browser on to
 * the relying party.
 *
 * Web-only: the native build routes to the stub in `sso-login-screen.tsx`.
 */
export function SsoLoginScreen() {
  const route = useRoute();
  const uid = ((route.params ?? {}) as { uid?: string }).uid ?? '';

  const setActive = useOidcInteractionStore(state => state.setActive);
  const isAuthenticated = useSessionStore(state => state.isAuthenticated);

  const [phase, setPhase] = useState<Phase>('resolving');
  // Guards the one-way handoff: once we start submitting we must not fire it again
  // (e.g. isAuthenticated re-renders) before the browser navigates away.
  const submittedRef = useRef(false);

  // Mark OIDC-interaction mode so `withUnAuth` keeps the UnAuth tree (and this screen)
  // mounted when Firebase flips the session to authenticated mid-flow.
  useEffect(() => {
    setActive(true);
    return () => setActive(false);
  }, [setActive]);

  const finishWithLogin = useCallback(async () => {
    if (submittedRef.current || !uid) return;
    const user = getAuthUser();
    if (!user?.emailVerified) return;
    submittedRef.current = true;
    setPhase('completing');
    try {
      const idToken = await getAuthToken();
      if (!idToken) throw new Error('Missing Firebase ID token');
      const { redirectTo } = await submitInteractionLogin(uid, idToken);
      window.location.assign(redirectTo);
    } catch (error) {
      submittedRef.current = false;
      captureException(
        { error_payload: error },
        { tags: { usk_log_type: 'oidc_interaction_login_error' } },
      );
      setPhase('error');
    }
  }, [uid]);

  // User declined the "continue as <email>" prompt (or any other abort): tell the IdP so the
  // relying party receives `access_denied`, then follow its redirect back to the origin.
  const abortAndReturn = useCallback(async () => {
    if (!uid) return;
    setPhase('completing');
    try {
      const { redirectTo } = await abortInteraction(uid);
      window.location.assign(redirectTo);
    } catch (error) {
      captureException(
        { error_payload: error },
        { tags: { usk_log_type: 'oidc_interaction_abort_error' } },
      );
      setPhase('error');
    }
  }, [uid]);

  // Complete a consent prompt with the guardian's `players`-scope choice: `true` shares the
  // children list, `false` signs in without it. Both resolve the interaction and hand the
  // browser on to the relying party.
  const completeConsent = useCallback(
    async (sharePlayers: boolean) => {
      if (submittedRef.current || !uid) return;
      submittedRef.current = true;
      setPhase('completing');
      try {
        const { redirectTo } = await confirmInteraction(uid, sharePlayers);
        window.location.assign(redirectTo);
      } catch (error) {
        submittedRef.current = false;
        captureException(
          { error_payload: error },
          { tags: { usk_log_type: 'oidc_interaction_consent_error' } },
        );
        setPhase('error');
      }
    },
    [uid],
  );

  // Resolve what the interaction needs. Consent is auto-handled unless it needs the guardian's
  // explicit approval to share their children list (the `players` scope). An already-signed-in
  // user is asked to confirm continuing as their current account (Continue → hand over the
  // token; Cancel → abort back to the RP); otherwise we fall back to rendering the login form.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!uid) {
        setPhase('error');
        return;
      }
      try {
        const details = await getInteraction(uid);
        if (cancelled) return;
        if (details.prompt === 'consent') {
          // The `players` scope (children list — child data) needs the guardian's explicit
          // approval: render the consent form and let them choose. All other scopes are
          // first-party auto-granted, so complete the consent silently.
          if (details.requiresPlayersConsent) {
            setPhase('awaiting-players-consent');
            return;
          }
          setPhase('completing');
          const { redirectTo } = await confirmInteraction(uid);
          window.location.assign(redirectTo);
          return;
        }
        // prompt === 'login'
        const user = getAuthUser();
        if (user?.emailVerified) {
          setPhase('awaiting-confirm');
          const email = user.email ?? '';
          const proceed = await showConfirm({
            title: '',
            message: email
              ? strings.confirmContinueWithEmail.replace('{email}', email)
              : strings.confirmContinue,
            confirmText: strings.confirmContinueButton,
            cancelText: strings.confirmCancelButton,
          });
          if (cancelled) return;
          if (proceed) {
            await finishWithLogin();
          } else {
            await abortAndReturn();
          }
          return;
        }
        setPhase('awaiting-login');
      } catch (error) {
        if (cancelled) return;
        captureException(
          { error_payload: error },
          { tags: { usk_log_type: 'oidc_interaction_details_error' } },
        );
        setPhase('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, finishWithLogin, abortAndReturn]);

  // Fresh login: once Firebase (email or social) reports a verified session, hand the token
  // to the IdP. Keyed off the session store so it lands only after AuthStateEffect has
  // settled the social-sync ordering.
  useEffect(() => {
    if (phase === 'awaiting-login' && isAuthenticated) {
      finishWithLogin();
    }
  }, [phase, isAuthenticated, finishWithLogin]);

  if (phase === 'awaiting-login') {
    return <WelcomeScreen />;
  }

  if (phase === 'awaiting-confirm') {
    // Neutral backdrop; the "Continue with email …" dialog (WebAlertHost) overlays this.
    return <View style={styles.center} />;
  }

  if (phase === 'awaiting-players-consent') {
    return (
      <SsoPlayersConsent
        onAllow={() => completeConsent(true)}
        onDecline={() => completeConsent(false)}
      />
    );
  }

  if (phase === 'error') {
    return (
      <View style={styles.center}>
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.label}>
          {strings.error}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.center}>
      <ActivityIndicator size="large" color={colors['10']} />
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.label}>
        {strings.completing}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  label: { marginTop: 16, textAlign: 'center', color: colors['10'] },
});

export default SsoLoginScreen;
